/**
 * Shared log table components for consistent presentation
 * across grouped and flat log views.
 */

import React from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { LogEvent } from '@/types/dashboard';
import { formatTimestamp, formatLogStreamName } from './formatters';
import { LogLevelBadge } from './LogLevelBadge';

// =============================================================================
// Table Shell
// =============================================================================

interface LogTableShellProps {
  children: React.ReactNode;
}

/**
 * Consistent table wrapper with standard header for log views
 */
export function LogTableShell({ children }: LogTableShellProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[32px]"></TableHead>
          <TableHead className="w-[180px]">Time</TableHead>
          <TableHead className="w-[80px]">Severity</TableHead>
          <TableHead>Message</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>{children}</TableBody>
    </Table>
  );
}

// =============================================================================
// Individual Log Row
// =============================================================================

interface LogRowProps {
  event: LogEvent;
  rowKey: string;
  isExpanded: boolean;
  onToggle: (key: string) => void;
}

/**
 * Single log entry row with expandable details
 */
export function LogRow({ event, rowKey, isExpanded, onToggle }: LogRowProps) {
  return (
    <React.Fragment>
      <TableRow
        className="cursor-pointer hover:bg-muted/50"
        onClick={() => onToggle(rowKey)}
      >
        <TableCell className="w-[32px] p-2">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </TableCell>
        <TableCell className="font-mono text-xs whitespace-nowrap">
          {formatTimestamp(event.timestamp, 'full')}
        </TableCell>
        <TableCell>
          <LogLevelBadge level={event.level} />
        </TableCell>
        <TableCell className="font-mono text-xs max-w-[500px] truncate">
          {event.message}
        </TableCell>
      </TableRow>
      {isExpanded && (
        <TableRow className="bg-muted/30 hover:bg-muted/30">
          <TableCell colSpan={4} className="p-4">
            <ExpandedLogContent event={event} />
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
}

// =============================================================================
// Expanded Log Content
// =============================================================================

interface ExpandedLogContentProps {
  event: LogEvent;
}

/**
 * Expanded view showing full message, structured data, stream name, and request ID
 */
export function ExpandedLogContent({ event }: ExpandedLogContentProps) {
  const hasData = event.data && Object.keys(event.data).length > 0;

  return (
    <div className="space-y-3">
      {/* Message */}
      <div className="font-mono text-xs whitespace-pre-wrap break-all bg-background p-3 rounded border">
        {event.message}
      </div>

      {/* Structured Data */}
      {hasData && (
        <div className="space-y-1">
          <span className="text-xs font-medium text-muted-foreground">
            Data:
          </span>
          <div className="font-mono text-xs whitespace-pre-wrap break-all bg-background p-3 rounded border">
            {formatStructuredData(event.data)}
          </div>
        </div>
      )}

      {/* Metadata */}
      <div className="flex gap-4 text-xs text-muted-foreground">
        <span>
          <span className="font-medium">Stream:</span>{' '}
          {formatLogStreamName(event.logStreamName)}
        </span>
        {event.requestId && (
          <span>
            <span className="font-medium">Request ID:</span> {event.requestId}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Format structured data for display
 */
function formatStructuredData(
  data: Record<string, unknown> | undefined,
): string {
  if (!data) return '';

  try {
    return JSON.stringify(data, null, 2);
  } catch {
    return String(data);
  }
}
